import React from "react";
import { useNavigate } from "react-router-dom";
import { NextBestWorkout } from "../../models/next-best-workout";

const PHASE_LABELS: Record<string, string> = {
  build: "Build phase",
  "general fitness": "General fitness",
  peak: "Peak phase",
  taper: "Taper",
};

const fmtSeconds = (seconds: number): string => {
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, "0")}m`;
};

/** The backend's threshold explanation ends with the test menu as prose; the
 *  card renders the richer bulleted version instead, so trim it off. */
const THRESHOLD_MENU_PREFIX = "You can refresh your threshold with any of these:";

interface RecommendationCardProps {
  recommendation: NextBestWorkout | null;
  loading: boolean;
}

export const RecommendationCard: React.FC<RecommendationCardProps> = ({
  recommendation,
  loading,
}) => {
  const navigate = useNavigate();

  if (loading || recommendation === null) {
    return (
      <section className="home__rec">
        <div className="home__rec-content">
          <div className="home__rec-overline">Recommended next</div>
          <div className="home__rec-body">
            {loading ? "Analyzing your recent training…" : "No recommendation available right now."}
          </div>
        </div>
      </section>
    );
  }

  const rec = recommendation;
  const isWorkout = rec.kind === "long_run" || rec.kind === "short_workout";

  const title =
    isWorkout && rec.duration_seconds
      ? `${rec.title}: ${fmtSeconds(rec.duration_seconds)} in Zone ${rec.zone_number}`
      : rec.title;

  const body =
    rec.kind === "threshold_test"
      ? rec.explanation.split(THRESHOLD_MENU_PREFIX)[0].trim()
      : rec.explanation;

  const metaParts: string[] = [];
  if (rec.phase) metaParts.push(PHASE_LABELS[rec.phase] ?? rec.phase);
  if (isWorkout && rec.zone_number != null && rec.zone_hr_range) {
    metaParts.push(`Zone ${rec.zone_number} · ${rec.zone_hr_range}`);
  }
  if (
    rec.kind !== "rest" &&
    rec.weekly_remaining_seconds != null &&
    rec.weekly_target_seconds != null
  ) {
    metaParts.push(
      `${fmtSeconds(rec.weekly_remaining_seconds)} left of ${fmtSeconds(rec.weekly_target_seconds)} weekly target`
    );
  }

  return (
    <section className="home__rec">
      <div className="home__rec-icon">
        {rec.kind === "rest" ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f0862f"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9z"></path>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f0862f"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 17l6-6 4 4 8-8M14 7h7v7"></path>
          </svg>
        )}
      </div>
      <div className="home__rec-content">
        <div className="home__rec-overline">Recommended next</div>
        <div className="home__rec-title">{title}</div>
        <div className="home__rec-body">{body}</div>
        {rec.kind === "threshold_test" && (
          <>
            <div className="home__rec-body" style={{ marginTop: "1.2rem" }}>
              You can refresh your threshold by doing one of the following
              tests:
            </div>
            <ul className="home__rec-tests">
              <li>
                <strong>A race or maximum effort of at least 60 minutes.</strong>{" "}
                This is the most direct measurement: your threshold pace and
                heart rate are taken from the fastest 60-minute stretch of the
                effort, with no extrapolation needed.
              </li>
              <li>
                <strong>A 30-minute threshold test.</strong> Warm up for 15
                minutes, then run 30 minutes all-out (about 45–60 minutes
                total). Your threshold pace and heart rate are taken from the
                fastest 30-minute segment after the warmup.
              </li>
              <li>
                <strong>A 20-minute threshold test.</strong> Warm up for 10
                minutes, then run 20 minutes all-out (about 30–45 minutes
                total). Your threshold pace and heart rate are taken from the
                fastest 20-minute segment after the warmup.
              </li>
              <li>
                <strong>A shorter race or max effort, such as a 5K.</strong>{" "}
                Any all-out effort under 30 minutes that covers more than 1.5
                km. Your threshold pace is estimated by projecting the effort
                out to 60 minutes, but your threshold heart rate can't be
                determined this way — you'll need one of the longer tests above
                to update that.
              </li>
            </ul>
          </>
        )}
        {metaParts.length > 0 && (
          <div className="home__rec-meta">{metaParts.join(" · ")}</div>
        )}
      </div>
      <div className="home__rec-buttons">
        {rec.kind !== "rest" && (
          <button
            className="home__btn-primary"
            onClick={() => navigate("/calendar")}
          >
            Add to calendar
          </button>
        )}
        <button
          className="home__btn-secondary"
          onClick={() => navigate("/chat")}
        >
          Ask Strongifier AI
        </button>
      </div>
    </section>
  );
};
